import { FlagImg } from "@faire/deprecated-retailer-visitor-shared/components/FlagImg";
import { InsiderTileBadgeRenderer } from "@faire/deprecated-retailer-visitor-shared/components/InsiderBadge/InsiderTileBadgeRenderer";
import { BrandStory } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/BrandStory";
import { getOrderMinimum } from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/brandTileUtils";
import {
  IBrandTileConfigurationProp,
  withConfigurationConsumer,
} from "@faire/deprecated-retailer-visitor-shared/components/LeanBrandTileRenderer/__internal__/configurableBrandTileUtils";
import { MaybeBrandReviewTile } from "@faire/deprecated-retailer-visitor-shared/components/MaybeBrandReviewTile/MaybeBrandReviewTile";
import { TrackedLink } from "@faire/deprecated-retailer-visitor-shared/components/TrackedLink/index";
import { DEFAULT_BRAND_TILE_PROMO_STYLE } from "@faire/deprecated-retailer-visitor-shared/features/Events/utils";
import { useInsiderState } from "@faire/deprecated-retailer-visitor-shared/hooks/insider/useInsiderState";
import { Core } from "@faire/slate/foundation/color";
import { IBrandTileBrandReviewsResponse } from "@faire/web-api--source/indigofair/data/IBrandTileBrandReviewsResponse";
import { IBrandTilePromoLabel } from "@faire/web-api--source/indigofair/data/IBrandTilePromoLabel";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { ILeanBrandTile } from "@faire/web-api--source/indigofair/data/ILeanBrandTile";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  localNum,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate-deprecated/Button";
import { LoadingSkeleton } from "@faire/web/slate-deprecated/LoadingSkeleton";
import { Star } from "@faire/web/slate/icons/foundation/Star";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { SpacerValueOrKey } from "@faire/web/slate/spacing/getSpacing";
import { Typography, TypographyVariants } from "@faire/web/slate/Typography";
import { AccessibleStarsDescription } from "@faire/web/ui/AccessibleStarsDescription";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { NoTranslate, StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import styled, { css } from "styled-components";

interface IProps extends IBrandTileConfigurationProp {
  className?: string;
  name?: string;
  minimum_order_amount?: IFaireMoney;
  reorderMinimumCents?: number;
  isFirstOrder?: boolean;
  hasNewProducts?: boolean;
  promoText?: string | undefined;
  promoTextColor?: string;
  promoTextBackground?: string | "transparent";
  linkLocation: string | undefined;
  token: string;
  brand?: ILeanBrandTile.IBrand;
  onLinkClick?: () => void;
  isLoggedIn: boolean;
  brandPromoLabel?: IBrandTilePromoLabel;
  featuredBrandPromoLabel?: IBrandTilePromoLabel;
  review?: IBrandTileBrandReviewsResponse.IBrandTileBrandReview;
  brandUrl?: string;
  bottomPadding?: SpacerValueOrKey; //to apply spacing for promo when it does not exist
  topPadding?: SpacerValueOrKey;
  showMinimum?: boolean;
  previousOrders?: number;
  hideBrandDescription?: boolean;
}
const NameAndMoreBase: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    brand,
    promoTextColor,
    promoTextBackground,
    isLoggedIn,
    review,
    name,
    promoText,
    brandUrl,
    hasNewProducts,
    linkLocation,
    token,
    onLinkClick,
    isFirstOrder,
    minimum_order_amount,
    configuration,
    bottomPadding,
    showMinimum,
    topPadding,
    previousOrders,
    hideBrandDescription,
  } = props;

  const reviewCommentMaxLines = useResponsiveValue({
    mobileAndAbove: 5,
    tabletAndAbove: 4,
  });

  const { isInsider: isRetailerInsider } = useInsiderState();
  const promo = (showCategoryBrandStory: boolean) => {
    return props.promoText || props.brandPromoLabel ? (
      <MarginWrapper
        justify={showCategoryBrandStory ? "flex-start" : "center"}
        align="center"
        $addMargin={!!props.brandPromoLabel || !showCategoryBrandStory}
      >
        {props.brandPromoLabel ? (
          <Promo
            text={props.brandPromoLabel.text}
            background={props.promoTextBackground}
            color={props.promoTextColor}
            center
            variant="paragraphSansRegular"
          />
        ) : (
          <Promo
            text={props.promoText}
            background={props.promoTextBackground}
            color={props.promoTextColor}
            center={!showCategoryBrandStory}
          />
        )}
      </MarginWrapper>
    ) : null;
  };

  const renderMakerInformation = () => {
    const showCategoryBrandStory =
      !isLoggedIn && !!brand?.description && !hideBrandDescription;
    const isEventPromo =
      promoTextColor !== DEFAULT_BRAND_TILE_PROMO_STYLE.promoText ||
      promoTextBackground !== DEFAULT_BRAND_TILE_PROMO_STYLE.lightBackground;
    const shouldShowReview = review?.average_rating && review?.total_reviews;
    const shouldCenterText = review ? false : !showCategoryBrandStory;
    const localizedReview = localNum(review?.average_rating, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    });

    //In both renderBrandNameAndPromo and shouldShowReview session, there is a place to display review.
    //one is checking for brand.avg_brand_review_rating and the other is checking for review.average_rating
    //if both are available, there will be duplicate reviews, we prefer to show the review at shouldShowReview session.

    return (
      <>
        {renderBrandNameAndPromo(shouldCenterText, !!shouldShowReview)}
        {shouldShowReview && localizedReview ? (
          <ReviewWrapper>
            <Spacer height="1x" />
            <Row>
              <AccessibleStarsDescription rating={localizedReview.toString()}>
                <Row>
                  <Star fill="faireBlack" fontSize="16px" />
                  <Spacer width={4} />
                  <Typography>{localizedReview}</Typography>
                </Row>
              </AccessibleStarsDescription>
              <Spacer width={4} />
              <Typography color={Core.text.subdued}>(</Typography>
              <Button
                variant="tertiary"
                tertiaryColor={Core.text.subdued}
                linkTo={brandUrl}
              >
                {localNum(review.total_reviews)}
              </Button>
              <Typography color={Core.text.subdued}>)</Typography>
            </Row>
            {review.review_comment ? (
              <>
                <Spacer height="1x" />
                <Typography maxLines={reviewCommentMaxLines}>
                  {wrapStringInQuotes(review.review_comment, strictLocalize)}
                </Typography>
                <Spacer height="1x" />
                <Typography color={Core.text.subdued}>
                  {`${review.user_first_name} (${review.retailer_location})`}
                </Typography>
              </>
            ) : (
              <BrandStory
                name={name}
                description={brand?.description}
                showCategoryBrandStory={showCategoryBrandStory}
                hasPromoText={!!promoText}
              />
            )}
          </ReviewWrapper>
        ) : (
          <>
            {isEventPromo ? (
              promo(showCategoryBrandStory)
            ) : (
              <PaddingWrapper>{promo(showCategoryBrandStory)}</PaddingWrapper>
            )}
            <PaddingWrapper>
              <BrandStory
                name={name}
                description={brand?.description}
                showCategoryBrandStory={showCategoryBrandStory}
                hasPromoText={!!promoText}
              />
            </PaddingWrapper>
          </>
        )}
      </>
    );
  };

  const renderBrandNameAndPromo = (
    shouldCenterText: boolean,
    shouldShowReview: boolean
  ) => {
    const showNewItemsText = hasNewProducts && name && !showMinimum;
    const brandNameLoading = (
      <Row justify="center">
        <LoadingSkeleton width="100px" height="20px" />
      </Row>
    );

    const brandMinimumLoading = (
      <Row justify="center">
        <LoadingSkeleton width="110px" height="20px" />
      </Row>
    );

    return (
      <PaddingWrapper>
        <TrackedLink
          token={token}
          onClick={() => {
            onLinkClick?.();
          }}
          to={linkLocation}
        >
          <Row justify="center" align="center" style={{ position: "relative" }}>
            <Column
              style={{ width: "100%", position: "relative" }}
              align="flex-start"
            >
              <Spacer height={topPadding ? topPadding : "1x"} />
              {name ? (
                <BrandName
                  align={shouldCenterText ? "center" : "left"}
                  truncate
                >
                  {brand?.region_flag_image?.url ? (
                    <FlagImg
                      src={brand.region_flag_image.url}
                      alt={brand.region_flag_image.alt_text}
                    />
                  ) : null}
                  <NoTranslate>{name}</NoTranslate>
                </BrandName>
              ) : (
                brandNameLoading
              )}
            </Column>
          </Row>
        </TrackedLink>

        <Spacer height={4} />
        <WrappingRow
          justify={shouldCenterText ? "center" : "flex-start"}
          align="center"
        >
          {/*Do not show reviews here if review is showing in the next section*/}
          {!shouldShowReview ? (
            <MaybeBrandReviewTile rating={brand?.avg_brand_review_rating} />
          ) : null}
          {(isLoggedIn || showMinimum) && !configuration.disableBrandMinimum ? (
            <>
              {name ? (
                <Typography align={shouldCenterText ? "center" : "start"}>
                  {getOrderMinimum(
                    isFirstOrder,
                    minimum_order_amount,
                    strictLocalize
                  )}
                </Typography>
              ) : (
                brandMinimumLoading
              )}
            </>
          ) : null}
          {showNewItemsText ? (
            <>
              {isLoggedIn ? <Spacer width="1x" /> : null}
              <Typography color={Core.text.subdued} truncate align="center">
                <StrictLocalMsg
                  id="CxL+5M"
                  defaultMessage="New Items!"
                  description={{
                    text: "Promo text saying there are new items for a brand",
                  }}
                />
              </Typography>
            </>
          ) : null}
        </WrappingRow>
        {!showNewItemsText && bottomPadding ? <Spacer height={4} /> : null}
        <WrappingRow justify={shouldCenterText ? "center" : "flex-start"}>
          {benefitLabel(shouldCenterText)}
        </WrappingRow>
        {previousOrders ? (
          <>
            <Spacer height="1/2x" />
            <WrappingRow justify="center">
              <Typography align="center" color={Core.text.subdued}>
                <StrictLocalMsg
                  id="ACWKD3"
                  defaultMessage="{previousOrdersNumber, plural, one {# recent order} other {# recent orders}}"
                  description={{
                    text: "number of recent previous orders for a brand",
                  }}
                  values={{ previousOrdersNumber: previousOrders }}
                />
              </Typography>
            </WrappingRow>
          </>
        ) : null}
      </PaddingWrapper>
    );
  };

  const benefitLabel = (shouldCenterText: boolean) => {
    if (props.featuredBrandPromoLabel) {
      return (
        <Promo
          text={props.featuredBrandPromoLabel.text}
          background={props.promoTextBackground}
          color={props.promoTextColor}
          center
          variant="paragraphSansRegular"
        />
      );
    }

    const brandHasInsiderBenefits =
      !!props.brand?.insider_benefits?.benefits?.length;
    const shouldShowInsiderBadging = isRetailerInsider;

    if (shouldShowInsiderBadging && brandHasInsiderBenefits) {
      return (
        <StyledInsiderTileBadge
          $shouldCenterText={shouldCenterText}
          insiderBenefits={props.brand?.insider_benefits}
          insiderLabelText={props.brand?.insider_label?.text}
        />
      );
    }

    return null;
  };

  return (
    <NameAndMoreInnerWrapper className={props.className}>
      {renderMakerInformation()}
    </NameAndMoreInnerWrapper>
  );
};

export const NameAndMore = withConfigurationConsumer(NameAndMoreBase);

const Promo: React.FC<{
  text?: string;
  background?: string | "transparent";
  color?: string;
  center: boolean;
  showFaireFundedDiscountTooltip?: boolean;
  variant?: TypographyVariants;
  marginTop?: string;
  padding?: string;
}> = ({
  text,
  background,
  color,
  center,
  variant = "paragraphSansMedium",
  marginTop,
  padding,
}) => {
  const isEventPromo =
    color !== DEFAULT_BRAND_TILE_PROMO_STYLE.promoText ||
    background !== DEFAULT_BRAND_TILE_PROMO_STYLE.lightBackground;

  return isEventPromo ? (
    <PromoWrapper
      $background={background}
      $marginTop={isEventPromo ? getSpacing("1x") : marginTop}
      $padding={isEventPromo ? getSpacing(4, "1x") : padding}
      $width={isEventPromo ? "fit-content" : undefined}
      $center={isEventPromo}
    >
      <Typography
        variant={isEventPromo ? "paragraphSansMedium" : variant}
        align={center ? "center" : undefined}
        color={color}
      >
        {text ?? null}
      </Typography>
    </PromoWrapper>
  ) : (
    <>
      <SaleStyledPromoOrDefault
        promoText={text}
        center={center}
        color={color}
      />
    </>
  );
};

const SaleStyledPromoOrDefault: React.FC<{
  promoText?: string;
  center?: boolean;
  color?: string;
}> = ({ promoText, center, color }) => {
  if (!promoText) {
    return null;
  }

  return (
    <Typography align={center ? "center" : undefined} color={color}>
      {promoText}
    </Typography>
  );
};

const NameAndMoreInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  padding: 0;
`;

const PaddingWrapper = styled(NameAndMoreInnerWrapper)`
  padding: 0 7.5%;
`;

const BrandName = styled(Typography).attrs({
  variant: "paragraphSansMedium",
})`
  align-items: flex-start;
  width: 100%;
`;

const WrappingRow = styled(Row).attrs({ align: "center" })`
  position: relative;
  flex-wrap: wrap;
  height: 100%;
`;

const StyledInsiderTileBadge = styled(InsiderTileBadgeRenderer)<{
  $shouldCenterText: boolean;
}>`
  text-align: ${(props) => (props.$shouldCenterText ? "center" : "start")};
  padding-top: 4px;
`;

const PromoWrapper = styled.div<{
  $background?: string | "transparent";
  $marginTop?: string;
  $padding?: string;
  $width?: string;
  $center?: boolean;
}>`
  background: ${({ $background }) => $background};
  padding: ${(props) => props.$padding};
  ${(props) =>
    props.$center
      ? css`
          margin: 0 auto;
        `
      : undefined}
  margin-top: ${(props) => props.$marginTop};
  ${(props) =>
    props.$width
      ? css`
          width: ${props.$width};
        `
      : undefined}
`;

const MarginWrapper = styled(Row)<{ $addMargin: boolean }>`
  margin: ${(props) => (props.$addMargin ? "0 7.5%" : "0")};
`;

const ReviewWrapper = styled(PaddingWrapper)`
  padding-bottom: 7.5%;
`;

const wrapStringInQuotes = (
  message: string,
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize(
    {
      id: "RpKsQx",
      defaultMessage: "“{quoteAsFullyTranslatedSentence}”",
      description: {
        text: "Wrapping a message written by a brand or retailer in localized quotations",
      },
    },
    { quoteAsFullyTranslatedSentence: message }
  );
